/**
 * WeekApprovalBar — Phase C1 state machine on the grid header.
 *
 * Draft → Submit (opens the problems panel; double-bookings are a hard block,
 * a changed pay rate needs a one-line reason) → Submitted → Approve (confirm
 * shows the open-post count) / Reject (reason). Approved shows the lock and the
 * "submitted and approved by the same person" marker when it applies; Payroll
 * can Reopen (unlock) with a reason while the hours are still unused.
 *
 * Reasons for a changed rate are collected from the submit 422 itself, which
 * names the shifts without their amounts — so a manager who cannot see pay
 * rates can still say why one changed.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  CheckCircle2, Lock, Send, Undo2, XCircle, AlertTriangle, Users2, History,
} from 'lucide-react';
import { schedulingAPI } from '../../lib/api';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { useAuth } from '../../context/AuthContext';
import { CorrectionDialog } from './CorrectionDialog';

const STATUS_LABEL = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
};

function StatusPill({ status, locked }) {
  const tone = status === 'approved'
    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    : status === 'submitted'
      ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
      : 'bg-surface-sunken text-fg-muted border-line';
  return (
    <Badge variant="outline" className={tone} data-testid="scheduling-week-status">
      {status === 'approved' && locked && <Lock className="h-3 w-3 mr-1" />}
      {STATUS_LABEL[status] || status}
      {status === 'approved' && locked ? ' · Hours locked' : ''}
    </Badge>
  );
}

export function WeekApprovalBar({ week, state, canApprove, onChanged }) {
  const perms = useEffectivePermissions();
  const { user } = useAuth();
  const isPayroll = perms.hasGrant && perms.hasGrant('payroll');
  const status = week?.status || 'draft';

  const [submitOpen, setSubmitOpen] = useState(false);
  const [pre, setPre] = useState(null);
  const [reasons, setReasons] = useState({});      // shift_day_id -> reason
  const [reasonBlocks, setReasonBlocks] = useState([]); // from submit 422
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [reason, setReason] = useState('');


  const openSubmit = async () => {
    setReasons({}); setReasonBlocks([]); setConflicts([]);
    setSubmitOpen(true);
    try {
      const { data } = await schedulingAPI.preSubmit(week.id);
      setPre(data);
    } catch {
      setPre(null);
    }
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      await schedulingAPI.submitWeek(week.id, reasons);
      toast.success('Submitted for approval');
      setSubmitOpen(false);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const blocked = detail?.blocked || [];
      const cf = blocked.filter((b) => b.code === 'double_booked');
      const rb = blocked.filter((b) => b.code === 'pay_rate_change_reason_required');
      setConflicts(cf);
      setReasonBlocks(rb);
      if (!cf.length && !rb.length) {
        toast.error(typeof detail === 'string' ? detail : 'Could not submit this week');
      } else if (cf.length) {
        toast.error(`${cf.length} double-booking${cf.length === 1 ? '' : 's'} must be fixed first`);
      } else {
        toast.message('A reason is needed for each changed pay rate');
      }
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async () => {
    setBusy(true);
    try {
      await schedulingAPI.approveWeek(week.id);
      toast.success('Week approved — hours locked');
      setApproveOpen(false); setReason('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await schedulingAPI.rejectWeek(week.id, reason.trim());
      toast.success('Returned to draft');
      setRejectOpen(false); setReason('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject');
    } finally { setBusy(false); }
  };

  const doUnlock = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await schedulingAPI.unlockWeek(week.id, reason.trim());
      toast.success('Unlocked — back to draft, approver notified');
      setUnlockOpen(false); setReason('');
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not unlock'));
    } finally { setBusy(false); }
  };

  const openPosts = pre?.open_post_count ?? state?.open_post_count ?? week?.open_post_count ?? 0;
  const reviewCount = state?.pay_rate_review?.payroll_flag_count
    ?? pre?.pay_rate_review?.payroll_flag_count ?? 0;

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="week-approval-bar">
      <StatusPill status={status} locked={week?.hours_locked} />

      {status === 'submitted' && state?.submitted_by && (
        <span className="text-xs text-fg-muted" data-testid="scheduling-submitted-by">
          Submitted by {state.actor_names?.[state.submitted_by] || state.submitted_by}
        </span>
      )}
      {status === 'draft' && state?.return_comment && (
        <span className="text-xs text-red-600 flex items-center gap-1.5"
          data-testid="scheduling-return-comment">
          <Undo2 className="h-3.5 w-3.5" /> Returned: {state.return_comment}
        </span>
      )}
      {reviewCount > 0 && (
        <span className="text-xs text-fg-muted" data-testid="scheduling-payroll-flag">
          {reviewCount} item{reviewCount === 1 ? '' : 's'} for payroll review
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        {status === 'draft' && (
          <Button size="sm" onClick={openSubmit} data-testid="week-submit-btn">
            <Send className="h-4 w-4 mr-1.5" /> Submit for approval
          </Button>
        )}
        {status === 'submitted' && canApprove && (
          <>
            <Button size="sm" variant="outline" onClick={() => { setReason(''); setRejectOpen(true); }}
              data-testid="week-reject-btn">
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
            <Button size="sm" onClick={() => { setReason(''); setApproveOpen(true); }}
              data-testid="week-approve-btn">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
            </Button>
          </>
        )}
        {status === 'approved' && week?.hours_locked && isPayroll && (
          <>
            <Button size="sm" variant="outline" onClick={() => setCorrectOpen(true)}
              data-testid="week-correct-btn">
              <History className="h-4 w-4 mr-1.5" /> Correct hours
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setReason(''); setUnlockOpen(true); }}
              data-testid="week-unlock-btn">
              <Undo2 className="h-4 w-4 mr-1.5" /> Reopen (unlock)
            </Button>
          </>
        )}
      </div>

      {/* Submit / problems panel */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-lg" data-testid="week-submit-dialog">
          <DialogHeader>
            <DialogTitle>Submit this week for approval</DialogTitle>
            <DialogDescription>
              Double-bookings must be fixed first. A changed pay rate needs a one-line reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {conflicts.length > 0 && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3"
                data-testid="submit-double-bookings">
                <div className="text-sm font-medium text-red-600 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> {conflicts.length} double-booking(s)
                </div>
                <ul className="mt-2 space-y-1 text-xs text-fg-muted">
                  {conflicts.map((c, i) => (
                    <li key={i} data-testid="submit-conflict-row">
                      {c.employee_name || c.employee_id}: {c.windows?.[0]?.join('–')} and{' '}
                      {c.windows?.[1]?.join('–')} on {c.dates?.join(' / ')} overlap.
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-red-600">
                  These cannot be overridden — adjust the times, then submit again.
                </p>
              </div>
            )}

            {reasonBlocks.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-2"
                data-testid="submit-rate-reasons">
                <div className="text-sm font-medium text-amber-600">
                  {reasonBlocks.length} pay rate(s) changed since the week was built
                </div>
                {reasonBlocks.map((b) => (
                  <div key={b.shift_day_id} className="space-y-1">
                    <label className="text-xs text-fg-muted">
                      Shift on {b.shift_date} — why did it change?
                    </label>
                    <Textarea rows={1}
                      data-testid={`rate-reason-${b.shift_day_id}`}
                      value={reasons[b.shift_day_id] || ''}
                      onChange={(e) => setReasons((r) => ({ ...r, [b.shift_day_id]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}

            {!conflicts.length && !reasonBlocks.length && (
              <div className="text-sm text-fg-muted space-y-1" data-testid="submit-preflight">
                <p>{openPosts} unfilled post{openPosts === 1 ? '' : 's'} on this week.</p>
                {reviewCount > 0 && <p>{reviewCount} item(s) for payroll review.</p>}
                {pre && pre.double_bookings?.length === 0 && <p>No double-bookings.</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubmitOpen(false)}>Cancel</Button>
            <Button onClick={doSubmit} disabled={busy || conflicts.length > 0}
              data-testid="week-submit-confirm">
              {busy ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve confirm with open-post count */}
      <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
        <AlertDialogContent data-testid="week-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Approve and lock this week?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {openPosts > 0
                ? `${openPosts} post${openPosts === 1 ? '' : 's'} on this week ${openPosts === 1 ? 'is' : 'are'} unfilled. Approving locks the hours.`
                : 'Approving locks the hours on every shift in this week.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doApprove}
              disabled={busy}
              data-testid="week-approve-confirm">
              {busy ? 'Approving…' : 'Approve and lock'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject with reason */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="week-reject-dialog">
          <DialogHeader>
            <DialogTitle>Return this week to draft</DialogTitle>
            <DialogDescription>A reason goes back to whoever submitted it.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="What needs to change?" data-testid="week-reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={doReject} disabled={busy} data-testid="week-reject-confirm">
              {busy ? 'Returning…' : 'Return to draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock (payroll) with reason */}
      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent data-testid="week-unlock-dialog">
          <DialogHeader>
            <DialogTitle>Unlock this approved week</DialogTitle>
            <DialogDescription>
              The approver is notified. If the hours have already been used, a correction is
              required instead.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being unlocked?" data-testid="week-unlock-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnlockOpen(false)}>Cancel</Button>
            <Button onClick={doUnlock} disabled={busy} data-testid="week-unlock-confirm">
              {busy ? 'Unlocking…' : 'Unlock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CorrectionDialog week={week} open={correctOpen} onOpenChange={setCorrectOpen}
        onChanged={() => onChanged?.()} />
    </div>
  );
}

export default WeekApprovalBar;
